import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../lib/apiClient";
import { CertType } from "../lib/types";

type CertTypesResponse = { cert_types: CertType[] };

export default function RegistrantFormPage() {
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");  // native date input -> YYYY-MM-DD
  const [grade, setGrade] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [sponsored, setSponsored] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");

  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Populate grade options from the union of required_for_grades across
    // cert types. Not authoritative (cert types only cover grades that have
    // required S&M), but a useful hint; user can still type any value.
    apiFetch<CertTypesResponse>("/api/v1/cert_types")
      .then(r => {
        const s = new Set<string>();
        (r.cert_types ?? []).forEach(ct => ct.required_for_grades.forEach(g => s.add(g)));
        setGradeOptions(Array.from(s).sort());
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setErrors({});

    const body: Record<string, unknown> = {
      registrant: {
        first_name: firstName,
        last_name: lastName,
        email,
        date_of_birth: toApiDob(dob),
        grade,
      },
    };

    const reg = body.registrant as Record<string, unknown>;
    if (mobileNumber) reg.mobile_number = mobileNumber;
    if (sponsored) reg.sponsored = true;
    if (showAddress) {
      if (addressLine1) reg.address_line_1 = addressLine1;
      if (addressLine2) reg.address_line_2 = addressLine2;
      if (city) reg.city = city;
      if (county) reg.county = county;
      if (postcode) reg.postcode = postcode;
      if (country) reg.country = country;
    }

    try {
      const resp = await apiFetch<{ pin?: string; email?: string; status?: string }>(
        "/api/v1/registrants",
        { method: "POST", body }
      );
      // If the API returned a PIN, navigate to the detail. Otherwise back to list
      // (e.g. when an AssociationRequest was created instead of a new user).
      if (resp?.pin) {
        navigate(`/registrants/${resp.pin}`);
      } else {
        navigate("/registrants");
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setError(`${e.code}: ${e.message}`);
        setErrors(e.errors ?? {});
      } else {
        setError(e instanceof Error ? e.message : "Request failed");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-xl space-y-4">
      <div>
        <Link to="/registrants" className="text-sm text-brand-700 hover:underline">← Back</Link>
        <h1 className="text-xl font-semibold mt-1">New registrant</h1>
        <p className="text-sm text-slate-500">Calls <span className="mono">POST /api/v1/registrants</span>. If the email matches an existing user, an association request is created instead of a new user.</p>
      </div>

      {error && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-3 text-sm">
          {error}
          {Object.keys(errors).length > 0 && (
            <ul className="mt-1 list-disc list-inside">
              {Object.entries(errors).map(([k, v]) => <li key={k}><b>{k}:</b> {v}</li>)}
            </ul>
          )}
        </div>
      )}

      <form onSubmit={submit} className="space-y-3 bg-white border border-slate-200 rounded p-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" required error={errors.first_name}>
            <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </Field>
          <Field label="Last name" required error={errors.last_name}>
            <input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={lastName} onChange={e => setLastName(e.target.value)} required />
          </Field>
        </div>

        <Field label="Email" required error={errors.email}>
          <input type="email" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={email} onChange={e => setEmail(e.target.value)} required />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date of birth" required error={errors.date_of_birth}>
            <input type="date" className="border border-slate-300 rounded px-2 py-1 text-sm" value={dob} onChange={e => setDob(e.target.value)} required />
          </Field>
          <Field label="Grade" required error={errors.grade}>
            <input
              type="text"
              list="grade-options"
              className="w-full border border-slate-300 rounded px-2 py-1 text-sm mono"
              value={grade}
              onChange={e => setGrade(e.target.value.toUpperCase())}
              placeholder="e.g. EMT"
              required
            />
            <datalist id="grade-options">
              {gradeOptions.map(g => <option key={g} value={g} />)}
            </datalist>
          </Field>
        </div>

        <Field label="Mobile number (optional)">
          <input type="tel" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} />
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={sponsored} onChange={e => setSponsored(e.target.checked)} />
          <span>Sponsored (consumes one licence from your organisation's pool)</span>
        </label>

        <button type="button" onClick={() => setShowAddress(v => !v)} className="text-xs text-brand-700 hover:underline">
          {showAddress ? "− Hide address" : "+ Add address (optional)"}
        </button>
        {showAddress && (
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <Field label="Address line 1"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={addressLine1} onChange={e => setAddressLine1(e.target.value)} /></Field>
            <Field label="Address line 2"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={addressLine2} onChange={e => setAddressLine2(e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={city} onChange={e => setCity(e.target.value)} /></Field>
              <Field label="County"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={county} onChange={e => setCounty(e.target.value)} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Postcode"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={postcode} onChange={e => setPostcode(e.target.value)} /></Field>
              <Field label="Country"><input type="text" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" value={country} onChange={e => setCountry(e.target.value)} /></Field>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="submit" className="bg-brand-600 text-white rounded px-3 py-2 text-sm" disabled={submitting}>
            {submitting ? "Creating…" : "Create registrant"}
          </button>
          <Link to="/registrants" className="text-sm text-slate-500 hover:text-slate-800 self-center">Cancel</Link>
        </div>
      </form>
    </div>
  );
}

function Field({
  label, children, error, required,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <div className="text-xs text-slate-600 mb-1">{label}{required && <span className="text-red-500"> *</span>}</div>
      {children}
      {error && <div className="text-red-600 text-xs mt-1">{error}</div>}
    </label>
  );
}

// The API expects dd/mm/yyyy. Native <input type="date"> gives yyyy-mm-dd.
function toApiDob(iso: string): string {
  if (!iso || iso.length !== 10) return iso;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
